import React from 'react';
import { motion } from 'framer-motion';
import { Calendar, ArrowRight, Clock, TrendingUp, Users, Building2, Zap, Shield, Target, Package, Layers, Brain, Database, Award, Star } from 'lucide-react';

const Blog = () => {
  const blogPosts = [
    // Composable Systems & Architecture
    {
      id: 1,
      title: "The Rise of Composable Enterprise Architecture: Why 30% More Revenue Awaits",
      excerpt: "Discover how composable architecture is revolutionizing enterprise systems, enabling 30% higher revenue growth through modular, API-first design and packaged business capabilities.",
      author: "",
      role: "",
      date: "January 20, 2025",
      readTime: "12 min read",
      category: "Composable Systems",
      image: "🧩",
      featured: true,
      content: {
        intro: "The future of enterprise architecture is composable. Organizations adopting composable business architecture report 30% higher revenue growth, 50% faster time-to-market, and significantly reduced technical debt.",
        sections: [
          {
            title: "What is Composable Architecture?",
            content: "Composable architecture represents a fundamental shift from monolithic systems to modular, API-first design. It's built on four core principles: Modularity (independent components), Autonomy (self-contained operations), Orchestration (seamless integration), and Discovery (easy component identification)."
          },
          {
            title: "Packaged Business Capabilities (PBCs)",
            content: "PBCs are the building blocks of composable systems. These pre-built, reusable components represent specific business functions and can be assembled, reassembled, and scaled independently to meet changing business needs."
          },
          {
            title: "The Business Impact",
            content: "Organizations implementing composable architecture see dramatic improvements: 30% faster innovation cycles, 40% reduction in development costs, 60% improvement in system flexibility, and 25% increase in developer productivity."
          }
        ]
      }
    },
    {
      id: 2,
      title: "From Monolith to Modular: A Fortune 500 Transformation Story",
      excerpt: "How a global manufacturing leader transformed their legacy systems into a composable architecture, achieving $2.4M in annual savings and 70% faster deployment cycles.",
      author: "",
      role: "",
      date: "January 18, 2025",
      readTime: "10 min read",
      category: "Case Study",
      image: "🏭",
      featured: true,
      content: {
        intro: "This Fortune 500 manufacturer was struggling with rigid legacy systems that couldn't adapt to market changes. Their transformation to composable architecture delivered remarkable results.",
        sections: [
          {
            title: "The Challenge",
            content: "Legacy monolithic systems across 15 countries, 6-month deployment cycles, $500K annual maintenance costs, and inability to integrate new technologies or respond to market changes quickly."
          },
          {
            title: "The Solution",
            content: "Implemented composable architecture with API-first design, created 47 packaged business capabilities, established microservices foundation, and deployed event-driven architecture for real-time operations."
          },
          {
            title: "The Results",
            content: "$2.4M annual savings, 70% faster deployments (6 months to 6 weeks), 85% reduction in integration time, 90% improvement in system uptime, and ability to launch new products 3x faster."
          }
        ]
      }
    },
    {
      id: 3,
      title: "API-First Design: The Foundation of Composable Enterprise",
      excerpt: "Learn how API-first architecture enables true composability, reduces integration complexity by 80%, and creates the foundation for scalable enterprise systems.",
      author: "",
      role: "",
      date: "January 15, 2025",
      readTime: "8 min read",
      category: "Technology",
      image: "🔗",
      featured: false,
      content: {
        intro: "API-first design is the cornerstone of composable architecture. It enables seamless integration, reduces complexity, and provides the flexibility needed for modern enterprise systems.",
        sections: [
          {
            title: "API-First Principles",
            content: "Design APIs before implementation, treat APIs as products, ensure consistency across all endpoints, implement comprehensive documentation, and establish robust governance frameworks."
          },
          {
            title: "Benefits for Enterprise",
            content: "80% reduction in integration complexity, 60% faster development cycles, improved system interoperability, enhanced developer experience, and simplified third-party integrations."
          }
        ]
      }
    },

    // Healthcare & Industry Solutions
    {
      id: 4,
      title: "Healthcare Digital Transformation: AquaCare Success Stories",
      excerpt: "How three major healthcare providers used AquaCare to reduce administrative costs by 60%, improve patient satisfaction by 40%, and streamline operations across multiple facilities.",
      author: "",
      role: "",
      date: "January 12, 2025",
      readTime: "9 min read",
      category: "Healthcare",
      image: "🏥",
      featured: false,
      content: {
        intro: "Healthcare organizations face unique challenges in digital transformation. Our AquaCare platform has helped numerous providers overcome these challenges with remarkable results.",
        sections: [
          {
            title: "Delhi Health Centre Transformation",
            content: "Implemented comprehensive patient management system, automated appointment scheduling, integrated billing processes, and achieved 60% reduction in administrative costs while improving patient satisfaction by 40%."
          },
          {
            title: "Multi-Facility Integration",
            content: "Connected 12 facilities under unified system, standardized processes across locations, implemented real-time data sharing, and reduced patient wait times by 45%."
          }
        ]
      }
    },
    {
      id: 5,
      title: "Financial Services Automation: AquaInvoice Impact Analysis",
      excerpt: "Discover how financial institutions are leveraging AquaInvoice to automate invoice processing, achieve 99.9% accuracy, and reduce processing time by 85%.",
      author:"",
      role: "",
      date: "January 10, 2025",
      readTime: "7 min read",
      category: "Finance",
      image: "💰",
      featured: false,
      content: {
        intro: "Financial services require precision, speed, and compliance. AquaInvoice delivers all three while dramatically reducing operational costs and improving accuracy.",
        sections: [
          {
            title: "Automation Benefits",
            content: "85% reduction in processing time, 99.9% accuracy in financial calculations, automated compliance reporting, real-time fraud detection, and seamless integration with existing financial systems."
          },
          {
            title: "ROI Analysis",
            content: "Average ROI of 340% within 12 months, $1.2M annual savings for mid-size institutions, 70% reduction in manual errors, and 50% improvement in audit preparation time."
          }
        ]
      }
    },

    // Technology & Innovation
    {
      id: 6,
      title: "AI-Powered Business Intelligence: The Future is Now",
      excerpt: "Explore how AI integration in business intelligence is transforming decision-making, providing predictive insights, and enabling autonomous business operations.",
      author: "",
      role: "",
      date: "January 8, 2025",
      readTime: "11 min read",
      category: "AI & Innovation",
      image: "🤖",
      featured: false,
      content: {
        intro: "Artificial Intelligence is revolutionizing business intelligence, moving from reactive reporting to predictive insights and autonomous decision-making.",
        sections: [
          {
            title: "Predictive Analytics",
            content: "AI-powered predictive models that forecast business trends, identify opportunities, predict customer behavior, and optimize resource allocation with 90% accuracy."
          },
          {
            title: "Autonomous Operations",
            content: "Self-managing systems that adapt to changing conditions, automatically optimize processes, detect and resolve issues before they impact business, and continuously improve performance."
          }
        ]
      }
    },
    {
      id: 7,
      title: "No-Code Revolution: Empowering Business Users to Build Enterprise Solutions",
      excerpt: "How no-code platforms are democratizing application development, enabling business users to create enterprise-grade solutions, and accelerating digital transformation.",
      author: "",
      role: "",
      date: "January 5, 2025",
      readTime: "9 min read",
      category: "No-Code",
      image: "⚡",
      featured: false,
      content: {
        intro: "The no-code revolution is empowering business users to become citizen developers, creating enterprise applications without traditional programming skills.",
        sections: [
          {
            title: "Citizen Developer Movement",
            content: "Business users creating applications, 5x faster development cycles, reduced IT backlog, improved business-IT alignment, and democratized innovation across organizations."
          },
          {
            title: "Enterprise-Grade Capabilities",
            content: "Security, scalability, compliance, integration capabilities, governance frameworks, and professional support ensuring enterprise readiness of no-code solutions."
          }
        ]
      }
    },

    // Security & Compliance
    {
      id: 8,
      title: "Enterprise Security in Composable Architecture: Best Practices Guide",
      excerpt: "Comprehensive guide to maintaining robust security across modular systems, implementing zero-trust architecture, and ensuring compliance in composable environments.",
      author: "",
      role: "",
      date: "January 3, 2025",
      readTime: "13 min read",
      category: "Security",
      image: "🛡️",
      featured: false,
      content: {
        intro: "Security in composable architecture requires a different approach. Learn how to implement comprehensive security across modular, distributed systems.",
        sections: [
          {
            title: "Zero-Trust Architecture",
            content: "Implement zero-trust principles, secure API gateways, identity and access management, continuous monitoring, and threat detection across all components."
          },
          {
            title: "Compliance Framework",
            content: "Maintain GDPR compliance, implement audit trails, ensure data protection, manage regulatory requirements, and establish governance frameworks for modular systems."
          }
        ]
      }
    },
    {
      id: 9,
      title: "Data Privacy in the Age of Automation: GDPR Compliance Made Simple",
      excerpt: "Navigate complex data privacy regulations while implementing automation solutions. Learn how to maintain GDPR compliance without sacrificing innovation.",
      author: "",
      role: "",
      date: "December 30, 2024",
      readTime: "8 min read",
      category: "Compliance",
      image: "📋",
      featured: false,
      content: {
        intro: "Data privacy regulations like GDPR can seem challenging when implementing automation. Here's how to maintain compliance while innovating.",
        sections: [
          {
            title: "Privacy by Design",
            content: "Implement privacy controls from the ground up, data minimization principles, consent management, right to erasure, and transparent data processing."
          },
          {
            title: "Automated Compliance",
            content: "Automated compliance monitoring, real-time privacy assessments, consent tracking, data mapping, and audit trail generation for regulatory reporting."
          }
        ]
      }
    },

    // Business Strategy & ROI
    {
      id: 10,
      title: "ROI Measurement in Enterprise Automation: A Complete Framework",
      excerpt: "Learn how to measure, track, and optimize return on investment for automation initiatives. Includes templates, metrics, and real-world examples.",
      author: "",
      role: "",
      date: "December 28, 2024",
      readTime: "10 min read",
      category: "Business Strategy",
      image: "📊",
      featured: false,
      content: {
        intro: "Measuring ROI in automation requires a comprehensive framework that captures both quantitative and qualitative benefits.",
        sections: [
          {
            title: "ROI Calculation Framework",
            content: "Direct cost savings, productivity improvements, error reduction benefits, time savings quantification, and long-term strategic value assessment."
          },
          {
            title: "Success Metrics",
            content: "Process efficiency gains, employee satisfaction improvements, customer experience enhancements, compliance improvements, and innovation acceleration metrics."
          }
        ]
      }
    },
    {
      id: 11,
      title: "Digital Transformation Strategy: From Vision to Implementation",
      excerpt: "A step-by-step guide to successful digital transformation, including change management, technology selection, and measuring success in enterprise environments.",
      author: "",
      role: "",
      date: "December 25, 2024",
      readTime: "14 min read",
      category: "Strategy",
      image: "🚀",
      featured: false,
      content: {
        intro: "Digital transformation is more than technology implementation. It's about fundamental business change that requires careful planning and execution.",
        sections: [
          {
            title: "Transformation Roadmap",
            content: "Assessment and planning phase, technology selection criteria, implementation phases, change management strategies, and success measurement frameworks."
          },
          {
            title: "Common Pitfalls",
            content: "Avoiding technology-first approaches, ensuring stakeholder buy-in, managing resistance to change, maintaining business continuity, and measuring the right metrics."
          }
        ]
      }
    },

    // Industry Trends & Future
    {
      id: 12,
      title: "Enterprise Technology Trends 2025: What Leaders Need to Know",
      excerpt: "Comprehensive analysis of emerging technology trends shaping enterprise operations, from AI and automation to composable architecture and edge computing.",
      author: "",
      role: "",
      date: "December 22, 2024",
      readTime: "12 min read",
      category: "Trends",
      image: "🔮",
      featured: false,
      content: {
        intro: "2025 brings significant technological shifts that will reshape how enterprises operate. Here are the key trends every leader should understand.",
        sections: [
          {
            title: "Emerging Technologies",
            content: "Generative AI integration, composable architecture adoption, edge computing expansion, quantum computing preparation, and sustainable technology initiatives."
          },
          {
            title: "Business Impact",
            content: "Operational efficiency improvements, new business model opportunities, enhanced customer experiences, competitive advantages, and risk mitigation strategies."
          }
        ]
      }
    },
    {
      id: 13,
      title: "The Future of Work: How Automation Enhances Human Potential",
      excerpt: "Explore how intelligent automation is reshaping the workplace, augmenting human capabilities, and creating new opportunities for employee growth and satisfaction.",
      author: "",
      role: "",
      date: "December 20, 2024",
      readTime: "9 min read",
      category: "Future of Work",
      image: "👥",
      featured: false,
      content: {
        intro: "Automation isn't replacing humans—it's augmenting human capabilities and creating opportunities for more meaningful, strategic work.",
        sections: [
          {
            title: "Human-AI Collaboration",
            content: "AI handling routine tasks, humans focusing on strategy and creativity, enhanced decision-making capabilities, improved work-life balance, and new skill development opportunities."
          },
          {
            title: "Workforce Transformation",
            content: "Reskilling and upskilling programs, new role definitions, enhanced job satisfaction, increased productivity, and improved employee engagement."
          }
        ]
      }
    },

    // Best Practices & Implementation
    {
      id: 14,
      title: "Building Scalable Workflows: Enterprise Automation Best Practices",
      excerpt: "Master the art of designing workflows that scale with your business. Learn from real implementations and avoid common pitfalls in enterprise automation.",
      author: "",
      role: "",
      date: "December 18, 2024",
      readTime: "11 min read",
      category: "Best Practices",
      image: "🔄",
      featured: false,
      content: {
        intro: "Scalable workflows are the foundation of successful enterprise automation. Learn how to design, implement, and optimize workflows that grow with your business.",
        sections: [
          {
            title: "Design Principles",
            content: "Modular workflow design, error handling strategies, performance optimization, scalability planning, and maintainability considerations."
          },
          {
            title: "Implementation Strategy",
            content: "Phased rollout approach, testing methodologies, monitoring and alerting, continuous improvement processes, and stakeholder training programs."
          }
        ]
      }
    },
    {
      id: 15,
      title: "Change Management in Digital Transformation: A Practical Guide",
      excerpt: "Successfully navigate organizational change during digital transformation. Proven strategies for stakeholder engagement, training, and adoption.",
      author: "",
      role: "",
      date: "December 15, 2024",
      readTime: "10 min read",
      category: "Change Management",
      image: "🔄",
      featured: false,
      content: {
        intro: "Technology is only half the equation in digital transformation. Successful change management is crucial for realizing the full benefits of automation initiatives.",
        sections: [
          {
            title: "Stakeholder Engagement",
            content: "Identifying key stakeholders, building coalition support, communication strategies, addressing resistance, and maintaining momentum throughout transformation."
          },
          {
            title: "Training and Adoption",
            content: "Comprehensive training programs, hands-on learning approaches, continuous support systems, measuring adoption rates, and celebrating early wins."
          }
        ]
      }
    }
  ];

  const categories = [
    "All", 
    "Composable Systems", 
    "Case Study", 
    "Technology", 
    "Healthcare", 
    "Finance", 
    "AI & Innovation", 
    "No-Code", 
    "Security", 
    "Compliance", 
    "Business Strategy", 
    "Strategy", 
    "Trends", 
    "Future of Work", 
    "Best Practices", 
    "Change Management"
  ];
  
  const [selectedCategory, setSelectedCategory] = React.useState("All");
  const [selectedPost, setSelectedPost] = React.useState(null);

  const filteredPosts = selectedCategory === "All" 
    ? blogPosts 
    : blogPosts.filter(post => post.category === selectedCategory);

  const featuredPosts = blogPosts.filter(post => post.featured);

  const handleReadMore = (post) => {
    setSelectedPost(post);
  };

  const handleBackToList = () => {
    setSelectedPost(null);
  };

  // If a post is selected, show the detailed view
  if (selectedPost) {
    return (
      <div className="min-h-screen bg-white">
        {/* Article Header */}
        <section className="relative bg-gradient-to-br from-primary/10 via-white to-secondary/5 py-20">
          <div className="container mx-auto px-6">
            <motion.button
              onClick={handleBackToList}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex items-center text-primary hover:text-secondary transition-colors mb-8"
            >
              <ArrowRight className="h-5 w-5 mr-2 rotate-180" />
              Back to Articles
            </motion.button>
            
            <div className="max-w-4xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center"
              >
                <div className="flex items-center justify-center space-x-4 mb-6">
                  <span className="bg-gradient-to-r from-primary/10 to-secondary/10 text-primary px-4 py-2 rounded-full text-sm font-semibold">
                    {selectedPost.category}
                  </span>
                  <div className="text-5xl">{selectedPost.image}</div>
                </div>
                
                <h1 className="text-4xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6">
                  {selectedPost.title}
                </h1>
                
                <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                  {selectedPost.excerpt}
                </p>
                
                <div className="flex items-center justify-center space-x-8 text-gray-500">
                  <div className="flex items-center">
                    <Calendar className="h-5 w-5 mr-2" />
                    {selectedPost.date}
                  </div>
                  <div className="flex items-center">
                    <Clock className="h-5 w-5 mr-2" />
                    {selectedPost.readTime}
                  </div>
                  <div className="flex items-center">
                    <Users className="h-5 w-5 mr-2" />
                    {selectedPost.author}
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Article Content */}
        <section className="py-20">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="prose prose-lg max-w-none"
              >
                <div className="bg-gradient-to-r from-primary/5 to-secondary/5 p-8 rounded-2xl mb-12">
                  <p className="text-xl text-gray-700 leading-relaxed font-medium">
                    {selectedPost.content.intro}
                  </p>
                </div>

                {selectedPost.content.sections.map((section, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 + index * 0.1 }}
                    className="mb-12"
                  >
                    <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
                      <div className="w-2 h-8 bg-gradient-to-b from-primary to-secondary rounded-full mr-4"></div>
                      {section.title}
                    </h2>
                    <p className="text-lg text-gray-700 leading-relaxed">
                      {section.content}
                    </p>
                  </motion.div>
                ))}

                {/* Author Bio */}
                <div className="bg-gray-50 p-8 rounded-2xl mt-16">
                  <div className="flex items-center space-x-4">
                    <div className="w-16 h-16 bg-gradient-to-r from-primary to-secondary rounded-full flex items-center justify-center text-white text-2xl font-bold">
                      {selectedPost.author.split(' ').map(n => n[0]).join('')}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">{selectedPost.author}</h3>
                      <p className="text-gray-600">{selectedPost.role}</p>
                      <p className="text-sm text-gray-500 mt-2">
                        Expert in enterprise automation and digital transformation with over 10 years of experience helping organizations achieve their technology goals.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Call to Action */}
                <div className="bg-gradient-to-r from-primary/10 to-secondary/10 p-8 rounded-2xl mt-12 text-center">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">
                    Ready to Transform Your Business?
                  </h3>
                  <p className="text-gray-600 mb-6">
                    Discover how Aquargin's composable platform can help your organization achieve similar results.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <motion.a
                      href="https://app.aquargin.com/register"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="bg-gradient-to-r from-primary-500 to-secondary-500 text-white px-8 py-4 rounded-lg font-semibold hover:shadow-xl transition-all duration-300 flex items-center justify-center"
                    >
                      Start Free Trial
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </motion.a>
                    <motion.a
                      href="/contact"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="border-2 border-primary text-primary px-8 py-4 rounded-lg font-semibold hover:bg-primary hover:text-white transition-all duration-300"
                    >
                      Schedule Demo
                    </motion.a>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary/10 via-white to-secondary/5 py-20 lg:py-32">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-secondary/5 opacity-40"></div>
        </div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-4xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6"
            >
              Enterprise Automation
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-primary-500 to-secondary-500">
                Insights & Expertise
              </span>
            </motion.h1>
            
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-xl lg:text-2xl text-gray-600 mb-8"
            >
              Stay ahead of the composable enterprise curve with expert insights, case studies, and thought leadership from industry pioneers.
            </motion.p>

            {/* Blog Stats */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="grid grid-cols-2 lg:grid-cols-4 gap-6 max-w-2xl mx-auto"
            >
              <div className="text-center">
                <div className="text-3xl font-bold text-primary">15+</div>
                <div className="text-sm text-gray-600">Expert Articles</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-secondary">8</div>
                <div className="text-sm text-gray-600">Categories</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-accent">5+</div>
                <div className="text-sm text-gray-600">Case Studies</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-green-600">Weekly</div>
                <div className="text-sm text-gray-600">Updates</div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Featured Posts */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6"
            >
              Featured Articles
            </motion.h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Deep-dive insights into composable systems, enterprise automation, and digital transformation strategies.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 mb-20">
            {featuredPosts.map((post, index) => (
              <motion.article
                key={post.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden group cursor-pointer"
                onClick={() => handleReadMore(post)}
              >
                <div className="p-8">
                  <div className="flex items-center justify-between mb-6">
                    <span className="bg-gradient-to-r from-primary/10 to-secondary/10 text-primary px-4 py-2 rounded-full text-sm font-semibold">
                      {post.category}
                    </span>
                    <div className="text-5xl">{post.image}</div>
                  </div>
                  
                  <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-primary transition-colors">
                    {post.title}
                  </h3>
                  
                  <p className="text-gray-600 mb-6 leading-relaxed">
                    {post.excerpt}
                  </p>
                  
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-gradient-to-r from-primary to-secondary rounded-full flex items-center justify-center text-white font-bold">
                        {post.author.split(' ').map(n => n[0]).join('')}
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">{post.author}</p>
                        <p className="text-sm text-gray-600">{post.role}</p>
                      </div>
                    </div>
                    
                    <div className="text-right">
                      <div className="flex items-center text-gray-500 text-sm mb-1">
                        <Calendar className="h-4 w-4 mr-1" />
                        {post.date}
                      </div>
                      <div className="flex items-center text-gray-500 text-sm">
                        <Clock className="h-4 w-4 mr-1" />
                        {post.readTime}
                      </div>
                    </div>
                  </div>
                  
                  <div className="pt-6 border-t border-gray-200">
                    <div className="flex items-center text-primary font-semibold group-hover:text-secondary transition-colors">
                      Read Full Article
                      <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* Category Filter & All Articles */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6"
            >
              All Articles
            </motion.h2>
            
            {/* Category Filters */}
            <div className="flex flex-wrap justify-center gap-3 mb-12 max-w-6xl mx-auto">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 rounded-lg font-semibold transition-all duration-300 text-sm ${
                    selectedCategory === category
                      ? 'bg-gradient-to-r from-primary-500 to-secondary-500 text-white shadow-lg'
                      : 'bg-white text-gray-700 hover:bg-gray-100 shadow-md'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {filteredPosts.map((post, index) => (
              <motion.article
                key={post.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden group cursor-pointer"
                onClick={() => handleReadMore(post)}
              >
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <span className="bg-gradient-to-r from-primary/10 to-secondary/10 text-primary px-3 py-1 rounded-full text-xs font-semibold">
                      {post.category}
                    </span>
                    <div className="text-3xl">{post.image}</div>
                  </div>
                  
                  <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-primary transition-colors line-clamp-2">
                    {post.title}
                  </h3>
                  
                  <p className="text-gray-600 mb-4 leading-relaxed text-sm line-clamp-3">
                    {post.excerpt}
                  </p>
                  
                  <div className="flex items-center justify-between text-xs text-gray-500 mb-4">
                    <div className="flex items-center">
                      <Calendar className="h-3 w-3 mr-1" />
                      {post.date}
                    </div>
                    <div className="flex items-center">
                      <Clock className="h-3 w-3 mr-1" />
                      {post.readTime}
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 bg-gradient-to-r from-primary to-secondary rounded-full flex items-center justify-center text-white text-xs font-bold">
                        {post.author.split(' ').map(n => n[0]).join('')}
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900 text-sm">{post.author}</p>
                        <p className="text-xs text-gray-600">{post.role}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center text-primary font-semibold group-hover:text-secondary transition-colors text-sm">
                      Read More
                      <ArrowRight className="ml-1 h-3 w-3 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter Signup */}
      <section className="py-20 bg-gradient-to-r from-primary-600 to-secondary-600">
        <div className="container mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-2xl mx-auto"
          >
            <h2 className="text-4xl font-bold text-white mb-6">
              Stay Updated with Enterprise Automation Insights
            </h2>
            <p className="text-xl text-white/90 mb-8">
              Get the latest articles on composable systems, automation strategies, and digital transformation delivered to your inbox weekly.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto mb-8">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 px-6 py-4 rounded-lg text-gray-900 font-medium focus:outline-none focus:ring-4 focus:ring-white/25"
              />
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-white text-primary px-8 py-4 rounded-lg font-bold hover:shadow-xl transition-all duration-300"
              >
                Subscribe
              </motion.button>
            </div>

            <div className="flex items-center justify-center space-x-6 text-white/80 text-sm">
              <div className="flex items-center">
                <Star className="h-4 w-4 mr-1" />
                Weekly insights
              </div>
              <div className="flex items-center">
                <Shield className="h-4 w-4 mr-1" />
                No spam, ever
              </div>
              <div className="flex items-center">
                <Target className="h-4 w-4 mr-1" />
                Unsubscribe anytime
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Blog; 